import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { bookingsApi, quotesApi } from '../../services/api';
import { ArrowRight, Loader2 } from 'lucide-react';
import { formatDate, formatCurrency } from '../../lib/utils';
import { Button, Kpi, Badge, Card, EmptyState } from '../../components/sv';
import { cargoLabel } from '../../components/booking/BulkFields';
import { EtaDisplay } from '../../components/shared/EtaDisplay';

const STATUS_LABEL = { pending: 'Pending', confirmed: 'Confirmed', in_transit: 'In transit', delivered: 'Delivered', cancelled: 'Cancelled' };

function UserDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(function () {
    Promise.all([bookingsApi.getAll(), quotesApi.getAll()])
      .then(function (r) { setBookings(r[0].data); setQuotes(r[1].data); })
      .catch(function () {})
      .finally(function () { setLoading(false); });
  }, []);

  const active = bookings.filter(function (b) { return b.status !== 'delivered' && b.status !== 'cancelled'; });
  const inTransit = bookings.filter(function (b) { return b.status === 'in_transit'; }).length;
  const pending = bookings.filter(function (b) { return b.status === 'pending'; }).length;
  const spend = bookings.filter(function (b) { return b.status !== 'cancelled'; }).reduce(function (s, b) { return s + (b.price || 0); }, 0);
  const attention = bookings.filter(function (b) { return b.status === 'confirmed' && b.payment_status !== 'paid'; }).slice(0, 3);

  return (
    <div className="grid gap-6" data-testid="user-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="sv-kicker">{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          <h1 className="text-[32px] sv-display mt-1">Good day, {user?.first_name}</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" to="/booking?mode=bulk" data-testid="new-bulk-booking-btn">Book bulk cargo</Button>
          <Button to="/booking" data-testid="new-booking-btn">Book a container</Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi label="Active shipments" value={active.length} data-testid="stat-active" />
        <Kpi label="In transit" value={inTransit} data-testid="stat-in-transit" />
        <Kpi label="Awaiting confirmation" value={pending} tone={pending ? 'bad' : undefined} delta={pending ? 'needs agent review' : undefined} data-testid="stat-pending" />
        <Kpi label="Total freight" value={formatCurrency(spend)} data-testid="stat-spend" />
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)] gap-6 items-start">
        <div className="sv-card" data-testid="recent-bookings">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-medium tracking-[-0.01em]">Recent bookings</h2>
            <Link to="/dashboard/bookings" className="sv-ink-link text-[15px]">All bookings <ArrowRight className="h-4 w-4" strokeWidth={1.75} /></Link>
          </div>
          {loading ? <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-600" /></div> : bookings.length === 0 ? (
            <EmptyState title="No bookings yet" text="Get a price and book your first shipment in a few minutes." action={<Button to="/booking">Book a container</Button>} />
          ) : (
            <div className="overflow-x-auto"><table className="sv-table">
              <thead><tr><th>Booking</th><th>Route</th><th>Cargo</th><th>Vessel</th><th>Status</th><th className="text-right">Freight</th></tr></thead>
              <tbody>
                {bookings.slice(0, 6).map(function (b) {
                  return (
                    <tr key={b.id} data-testid={"recent-booking-" + b.id}>
                      <td><Link to="/dashboard/bookings" className="sv-mono text-sm">{b.booking_number}</Link></td>
                      <td><div className="text-[15px] whitespace-nowrap">{b.origin_port.split(',')[0]} → {b.destination_port.split(',')[0]}</div><div className="text-[13px] text-gray-600"><EtaDisplay booking={b} prefix="ETA " testId={"dash-eta-" + b.booking_number} /></div></td>
                      <td className="text-[15px] capitalize whitespace-nowrap">{cargoLabel(b)}</td>
                      <td className="text-[15px]">{b.vessel_name || <span className="text-gray-600">Pending</span>}</td>
                      <td><Badge status={b.status}>{STATUS_LABEL[b.status] || b.status}</Badge></td>
                      <td className="text-right sv-mono text-sm">{formatCurrency(b.price)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          )}
        </div>

        <div className="grid gap-6">
          <Card title="Needs your attention" data-testid="attention-card">
            {attention.length === 0 ? <p className="text-[15px] text-gray-700">Nothing outstanding. Confirmed bookings awaiting payment appear here.</p> : (
              <div className="grid gap-3">
                {attention.map(function (b) {
                  return (
                    <div key={b.id} className="flex items-center justify-between gap-3 border-t border-gray-200 pt-3 first:border-0 first:pt-0">
                      <div><div className="sv-mono text-sm">{b.booking_number}</div><div className="text-[13px] text-gray-600">Payment due · {formatCurrency(b.price)}</div></div>
                      <Button size="sm" to={'/dashboard/payments/' + b.id}>Pay</Button>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
          <Card title="Recent quotes" action={<Link to="/dashboard/quotes" className="text-sm text-gray-600 hover:text-ink">All quotes</Link>} data-testid="recent-quotes">
            {quotes.length === 0 ? <p className="text-[15px] text-gray-700">No quotes yet. <Link to="/quote" className="sv-link">Get a price</Link>.</p> : (
              <div className="grid gap-3">
                {quotes.slice(0, 4).map(function (q) {
                  return (
                    <div key={q.id} className="flex items-center justify-between gap-3 border-t border-gray-200 pt-3 first:border-0 first:pt-0 text-[15px]">
                      <div><div>{q.origin_port.split(',')[0]} → {q.destination_port.split(',')[0]}</div><div className="text-[13px] text-gray-600">Valid until {formatDate(q.valid_until)}</div></div>
                      <span className="sv-mono text-sm">{formatCurrency(q.total_price)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

export default UserDashboard;
